import {
  LoyaltyAccount,
  LoyaltyProgramEnum,
} from "@/generated/email_parser.openapi";
import { HotelChain } from "@/generated/search.openapi";

/**
 * Maps loyalty program enum values to user-facing display names.
 * Only programs whose enum value differs from their display name need an entry.
 */
const loyaltyProgramDisplayNames: Partial<Record<LoyaltyProgramEnum, string>> =
  {
    [LoyaltyProgramEnum.MileagePlan]: "Atmos Rewards",
    [LoyaltyProgramEnum.HertzGoldPlusRewards]: "Hertz Gold+",
  };

export const getLoyaltyProgramDisplayName = (
  loyaltyProgram?: LoyaltyProgramEnum | string | null
): string => {
  if (!loyaltyProgram) return "";
  return (
    loyaltyProgramDisplayNames[loyaltyProgram as LoyaltyProgramEnum] ??
    loyaltyProgram
  );
};

/**
 * Programs whose redemption surface is "credits for free days" (step
 * function), not "cents per point" (linear). The linear cash-value
 * formula in `cash_value_for_points`
 * (api_email_parser/models/loyalty_perks.py) is structurally wrong for
 * these — see GON-5668 decision C.
 */
const FREE_DAY_LOYALTY_PROGRAMS: ReadonlySet<string> = new Set([
  LoyaltyProgramEnum.EmeraldClub,
]);

export const isFreeDayLoyaltyProgram = (
  loyaltyProgram?: LoyaltyProgramEnum | string | null
): boolean => {
  if (!loyaltyProgram) return false;
  return FREE_DAY_LOYALTY_PROGRAMS.has(loyaltyProgram);
};

/**
 * User-facing unit label for a program's balance. "credits" for
 * free-day programs (Emerald Club), "points" for everything else.
 */
export const getLoyaltyProgramUnit = (
  loyaltyProgram?: LoyaltyProgramEnum | string | null
): "credits" | "points" =>
  isFreeDayLoyaltyProgram(loyaltyProgram) ? "credits" : "points";

/**
 * Earning unit label pluralized for `count`: "1 credit" / "2 credits",
 * "1 point" / "1,932 points". Use wherever a count is shown next to the unit.
 */
export const getLoyaltyEarningUnitLabel = (
  count: number,
  loyaltyProgram?: LoyaltyProgramEnum | string | null
): string => {
  const unit = getLoyaltyProgramUnit(loyaltyProgram);
  return count === 1 ? unit.slice(0, -1) : unit;
};

export const getLoyaltyProgramLogo = (loyaltyProgram?: LoyaltyProgramEnum) => {
  switch (loyaltyProgram) {
    case LoyaltyProgramEnum.AAdvantage:
      return "/assets/loyalty-logos/aadvantage.svg";
    case LoyaltyProgramEnum.HiltonHonors:
      return "/assets/loyalty-logos/hilton-honors.svg";
    case LoyaltyProgramEnum.IhgOneRewards:
      return "/assets/loyalty-logos/ihg-one-rewards.svg";
    case LoyaltyProgramEnum.MarriottBonvoy:
      return "/assets/loyalty-logos/marriott-bonvoy.svg";
    case LoyaltyProgramEnum.MileagePlan:
      return "/assets/loyalty-logos/mileage-plan.svg";
    case LoyaltyProgramEnum.MileagePlus:
      return "/assets/loyalty-logos/mileage-plus.svg";
    case LoyaltyProgramEnum.SkyMiles:
      return "/assets/loyalty-logos/skymiles.svg";
    default:
      return "";
  }
};

export type BackGroundStyleMapping = {
  [key in 5 | 4 | 3 | 2 | 1 | 0]: {
    background: string;
  };
};

export const loyaltyCardBackgroundStyle: BackGroundStyleMapping = {
  5: { background: "var(--gradient-loyalty-card-tier-5)" },
  4: { background: "var(--gradient-card-titanium)" },
  3: { background: "var(--gradient-card-gold)" },
  2: { background: "var(--gradient-card-default)" },
  1: { background: "var(--gradient-card-bronze)" },
  0: { background: "var(--gradient-loyalty-card-tier-0)" },
};

export const loyaltyCardtagBackgroundStyle: BackGroundStyleMapping = {
  5: {
    background: "linear-gradient(90deg, #9F9C9D 0.44%, #6E6D6B 97.16%)",
  },
  4: {
    background: "linear-gradient(269deg, #174365 0.74%, #5C636A 99.21%)",
  },
  3: {
    background: "linear-gradient(88deg, #CB9872 1.06%, #5E4B3D 97.89%)",
  },
  2: {
    background: "linear-gradient(89deg, #9CB8C9 0.75%, #567786 97.3%)",
  },
  1: {
    background: "linear-gradient(90deg, #EB9971 -1.16%, #BA521F 99.9%)",
  },
  0: {
    background: "rgba(0, 0, 0, 0.40)",
  },
};

export const loyaltyAccountsColorMapping: {
  [key in LoyaltyProgramEnum]: string;
} = {
  [LoyaltyProgramEnum.AAdvantage]: "hsl(var(--loyalty-aadvantage))",
  [LoyaltyProgramEnum.HiltonHonors]: "hsl(var(--loyalty-hilton-honors))",
  [LoyaltyProgramEnum.IhgOneRewards]: "hsl(var(--loyalty-ihg-one-rewards))",
  [LoyaltyProgramEnum.MarriottBonvoy]: "hsl(var(--loyalty-marriott-bonvoy))",
  [LoyaltyProgramEnum.MileagePlan]: "hsl(var(--loyalty-mileage-plan))",
  [LoyaltyProgramEnum.MileagePlus]: "hsl(var(--loyalty-mileage-plus))",
  [LoyaltyProgramEnum.SkyMiles]: "hsl(var(--loyalty-skymiles))",
  [LoyaltyProgramEnum.WorldOfHyatt]: "hsl(var(--loyalty-world-of-hyatt))",
  [LoyaltyProgramEnum.TrueBlue]: "hsl(var(--loyalty-true-blue))",
  [LoyaltyProgramEnum.RapidRewards]: "hsl(var(--loyalty-rapid-rewards))",
  [LoyaltyProgramEnum.Gold]: "",
  [LoyaltyProgramEnum.AmexEveryDay]: "",
  [LoyaltyProgramEnum.ThePlatinumCard]: "",
  [LoyaltyProgramEnum.BlueBusinessPlus]: "",
  [LoyaltyProgramEnum.BusinessPlatinumCard]: "",
  [LoyaltyProgramEnum.ChaseFreedom]: "",
  [LoyaltyProgramEnum.ChaseFreedomFlex]: "",
  [LoyaltyProgramEnum.ChaseFreedomRise]: "",
  [LoyaltyProgramEnum.ChaseFreedomUnlimited]: "",
  [LoyaltyProgramEnum.ChaseSapphire]: "",
  [LoyaltyProgramEnum.ChaseSapphirePreferred]: "",
  [LoyaltyProgramEnum.ChaseSapphireReserve]: "",
  [LoyaltyProgramEnum.Bilt]: "",
  [LoyaltyProgramEnum.WyndhamRewards]: "hsl(var(--loyalty-wyndham-rewards))",
  [LoyaltyProgramEnum.AccorLiveLimitless]:
    "hsl(var(--loyalty-accor-live-limitless))",
  [LoyaltyProgramEnum.InkBusinessCash]: "",
  [LoyaltyProgramEnum.InkBusinessPremier]: "",
  [LoyaltyProgramEnum.InkBusinessUnlimited]: "",
  [LoyaltyProgramEnum.InkBusinessPlus]: "",
  [LoyaltyProgramEnum.InkBusinessPreferred]: "",
  [LoyaltyProgramEnum.ChoicePrivilegesRewards]:
    "hsl(var(--loyalty-choice-privileges))",
  [LoyaltyProgramEnum.BestWesternRewards]: "hsl(var(--loyalty-best-western))",
  [LoyaltyProgramEnum.BiltRewards]: "",
  [LoyaltyProgramEnum.ChaseUltimateRewards]: "",
  [LoyaltyProgramEnum.MembershipRewards]: "",
  [LoyaltyProgramEnum.BarclaycardRewards]: "",
  [LoyaltyProgramEnum.BarclaycardRewardsProgram]: "",
  [LoyaltyProgramEnum.BankOfAmericaPreferredRewards]: "",
  [LoyaltyProgramEnum.KrisFlyerPartner]: "hsl(var(--loyalty-kris-flyer))",
  [LoyaltyProgramEnum.AsiaMiles]: "hsl(var(--loyalty-asia-miles))",
  [LoyaltyProgramEnum.LeadersClub]: "hsl(var(--loyalty-leaders-club))",
  [LoyaltyProgramEnum.FlyingBlue]: "hsl(var(--loyalty-flying-blue))",
  [LoyaltyProgramEnum.CitiThankYouRewards]: "",
  [LoyaltyProgramEnum.BankOfAmericaTravelRewards]: "",
  [LoyaltyProgramEnum.CapitalOneRewards]: "",
  [LoyaltyProgramEnum.Aeroplan]: "hsl(var(--loyalty-aeroplan))",
  [LoyaltyProgramEnum.TheBritishAirwaysClub]:
    "hsl(var(--loyalty-executive-club))",
  [LoyaltyProgramEnum.FlyingClub]: "hsl(var(--loyalty-flying-club))",
  [LoyaltyProgramEnum.AerClub]: "hsl(var(--loyalty-aer-club))",
  [LoyaltyProgramEnum.EmiratesSkywards]:
    "hsl(var(--loyalty-emirates-skywards))",
  [LoyaltyProgramEnum.PrivilegeClub]: "hsl(var(--loyalty-privilege-club))",
  [LoyaltyProgramEnum.AviancaLifeMiles]:
    "hsl(var(--loyalty-avianca-life-miles))",
  [LoyaltyProgramEnum.Skypass]: "hsl(var(--loyalty-skypass))",
  [LoyaltyProgramEnum.AeromexicoRewards]: "hsl(var(--loyalty-aeromexico))",
  [LoyaltyProgramEnum.AnaMileageClub]: "hsl(var(--loyalty-ana-mileage))",
  [LoyaltyProgramEnum.MilesMore]: "hsl(var(--loyalty-miles-more))",
  [LoyaltyProgramEnum.MilesSmiles]: "",
  [LoyaltyProgramEnum.IberiaPlus]: "hsl(var(--loyalty-iberia-plus))",
  [LoyaltyProgramEnum.QantasFrequentFlyer]: "hsl(var(--loyalty-qantas))",
  [LoyaltyProgramEnum.VentureOneRewards]: "",
  [LoyaltyProgramEnum.VentureRewards]: "",
  [LoyaltyProgramEnum.VentureXRewards]: "",
  [LoyaltyProgramEnum.FrontierMiles]: "hsl(var(--loyalty-frontier-miles))",
  [LoyaltyProgramEnum.CitiDoubleCash]: "",
  [LoyaltyProgramEnum.CitiPremier]: "",
  [LoyaltyProgramEnum.AmericanExpressGreen]: "",
  [LoyaltyProgramEnum.BusinessGold]: "",
  [LoyaltyProgramEnum.BlueFromAmericanExpress]: "",
  [LoyaltyProgramEnum.SmartRewards]: "",
  [LoyaltyProgramEnum.CorporatePlatinum]: "",
  [LoyaltyProgramEnum.CorporateGold]: "",
  [LoyaltyProgramEnum.BusinessGreen]: "",
  [LoyaltyProgramEnum.CenturionCard]: "",
  [LoyaltyProgramEnum.ChaseSlate]: "",
  [LoyaltyProgramEnum.ChaseSlateEdge]: "",
  [LoyaltyProgramEnum.InkPlusBusiness]: "",
  [LoyaltyProgramEnum.CitiSimplicity]: "",
  [LoyaltyProgramEnum.CitiThankYouPreferred]: "",
  [LoyaltyProgramEnum.CitiPrestige]: "",
  [LoyaltyProgramEnum.CitiDiamondPreferred]: "",
  [LoyaltyProgramEnum.CitiRewards]: "",
  [LoyaltyProgramEnum.BankOfAmericaCashRewards]: "",
  [LoyaltyProgramEnum.BankOfAmericaPremiumRewards]: "",
  [LoyaltyProgramEnum.BusinessAdvantageTravelRewards]: "",
  [LoyaltyProgramEnum.BusinessAdvantageCashRewards]: "",
  [LoyaltyProgramEnum.ArrivalPlus]: "",
  [LoyaltyProgramEnum.Arrival]: "",
  [LoyaltyProgramEnum.UsBankAltitudeReserveVisaInfinite]: "",
  [LoyaltyProgramEnum.BankAmericard]: "",
  // Rental car loyalty programs — use loyalty- token prefix for consistency
  [LoyaltyProgramEnum.HertzGoldPlusRewards]: "hsl(var(--loyalty-hertz))",
  [LoyaltyProgramEnum.AvisPreferred]: "hsl(var(--loyalty-avis))",
  [LoyaltyProgramEnum.EmeraldClub]: "hsl(var(--loyalty-national))",
  [LoyaltyProgramEnum.EnterprisePlus]: "hsl(var(--loyalty-enterprise))",
  [LoyaltyProgramEnum.BudgetFastbreak]: "hsl(var(--loyalty-budget))",
  [LoyaltyProgramEnum.SixtPlus]: "hsl(var(--loyalty-sixt))",
  [LoyaltyProgramEnum.AlamoInsiders]: "hsl(var(--loyalty-alamo))",
};

/**
 * Hex color values for loyalty programs — used by SVG icon components that
 * need actual hex colors for gradient computations (tintHex / shadeHex).
 * Keep in sync with loyaltyAccountsColorMapping.
 */
export const loyaltyHexColorMapping: Partial<
  Record<LoyaltyProgramEnum, string>
> = {
  [LoyaltyProgramEnum.AAdvantage]: "#0078D2",
  [LoyaltyProgramEnum.HiltonHonors]: "#1E4380",
  [LoyaltyProgramEnum.IhgOneRewards]: "#1F4456",
  [LoyaltyProgramEnum.MarriottBonvoy]: "#B41F3A",
  [LoyaltyProgramEnum.MileagePlan]: "#003366",
  [LoyaltyProgramEnum.MileagePlus]: "#0C2340",
  [LoyaltyProgramEnum.SkyMiles]: "#E31837",
  [LoyaltyProgramEnum.WorldOfHyatt]: "#4B77A9",
  [LoyaltyProgramEnum.TrueBlue]: "#0039A6",
  [LoyaltyProgramEnum.RapidRewards]: "#1A1A1A",
  [LoyaltyProgramEnum.WyndhamRewards]: "#1F2C5D",
  [LoyaltyProgramEnum.AccorLiveLimitless]: "#1E1852",
  [LoyaltyProgramEnum.ChoicePrivilegesRewards]: "#E26812",
  [LoyaltyProgramEnum.BestWesternRewards]: "#BB943E",
  [LoyaltyProgramEnum.KrisFlyerPartner]: "#E4002B",
  [LoyaltyProgramEnum.AsiaMiles]: "#E4002B",
  [LoyaltyProgramEnum.LeadersClub]: "#13212E",
  [LoyaltyProgramEnum.FlyingBlue]: "#0039A6",
  [LoyaltyProgramEnum.Aeroplan]: "#E31837",
  [LoyaltyProgramEnum.TheBritishAirwaysClub]: "#E4002B",
  [LoyaltyProgramEnum.FlyingClub]: "#E4002B",
  [LoyaltyProgramEnum.AerClub]: "#E4002B",
  [LoyaltyProgramEnum.EmiratesSkywards]: "#D71920",
  [LoyaltyProgramEnum.PrivilegeClub]: "#E4002B",
  [LoyaltyProgramEnum.AviancaLifeMiles]: "#FF0000",
  [LoyaltyProgramEnum.Skypass]: "#0039A6",
  [LoyaltyProgramEnum.AeromexicoRewards]: "#E4002B",
  [LoyaltyProgramEnum.AnaMileageClub]: "#E4002B",
  [LoyaltyProgramEnum.MilesMore]: "#E4002B",
  [LoyaltyProgramEnum.IberiaPlus]: "#E4002B",
  [LoyaltyProgramEnum.QantasFrequentFlyer]: "#E4002B",
  [LoyaltyProgramEnum.FrontierMiles]: "#0039A6",
  // Rental car programs
  [LoyaltyProgramEnum.HertzGoldPlusRewards]: "#FFD200",
  [LoyaltyProgramEnum.AvisPreferred]: "#D4002A",
  [LoyaltyProgramEnum.EmeraldClub]: "#006747",
  [LoyaltyProgramEnum.EnterprisePlus]: "#007A53",
  [LoyaltyProgramEnum.BudgetFastbreak]: "#F37021",
  [LoyaltyProgramEnum.SixtPlus]: "#EC4D13",
  [LoyaltyProgramEnum.AlamoInsiders]: "#3083E8",
};

export const loyaltyEnumFromHotelChain = (
  chain?: string | null
): LoyaltyProgramEnum | undefined => {
  // Keep these alternate names aligned with common/hotel_chains.py. Recent
  // hotel history carries the backend's raw chain name, not a HotelChain enum.
  switch (chain?.trim().toLowerCase()) {
    case HotelChain.Marriott.toLowerCase():
    case "marriott international":
      return LoyaltyProgramEnum.MarriottBonvoy;
    case HotelChain.Hilton.toLowerCase():
    case "hilton worldwide":
    case "hilton hotels & resorts":
      return LoyaltyProgramEnum.HiltonHonors;
    case HotelChain.Hyatt.toLowerCase():
    case "hyatt hotels":
      return LoyaltyProgramEnum.WorldOfHyatt;
    case HotelChain.Ihg.toLowerCase():
    case "ihg hotels & resorts":
      return LoyaltyProgramEnum.IhgOneRewards;
    case HotelChain.Wyndham.toLowerCase():
    case "wyndham hotels & resorts":
    case "wyndham extra holidays":
      return LoyaltyProgramEnum.WyndhamRewards;
    case HotelChain.Choice.toLowerCase():
    case "choice hotels international":
      return LoyaltyProgramEnum.ChoicePrivilegesRewards;
    case HotelChain.Accor.toLowerCase():
      return LoyaltyProgramEnum.AccorLiveLimitless;
    case HotelChain.BestWestern.toLowerCase():
      return LoyaltyProgramEnum.BestWesternRewards;
    default:
      return undefined;
  }
};

/**
 * Maps a rental car vendor code to the corresponding loyalty program enum.
 * Vendor codes from Travelport (e.g., "ZE" = Hertz, "AV" = Avis).
 */
export const loyaltyEnumFromVendorCode = (
  vendorCode: string
): LoyaltyProgramEnum | undefined => {
  switch (vendorCode) {
    case "ZE":
    case "HZ":
      return LoyaltyProgramEnum.HertzGoldPlusRewards;
    case "AV":
    case "ZI":
      return LoyaltyProgramEnum.AvisPreferred;
    case "ZL":
      return LoyaltyProgramEnum.EmeraldClub;
    case "ET":
      return LoyaltyProgramEnum.EnterprisePlus;
    case "BU":
    case "ZD":
      return LoyaltyProgramEnum.BudgetFastbreak;
    case "SX":
      return LoyaltyProgramEnum.SixtPlus;
    case "AL":
      return LoyaltyProgramEnum.AlamoInsiders;
    default:
      return undefined;
  }
};

/**
 * Safely converts a string to LoyaltyProgramEnum
 * Returns undefined if the string doesn't match any enum value
 */
export function stringToLoyaltyProgramEnum(
  program?: string | null
): LoyaltyProgramEnum | undefined {
  if (!program) return undefined;

  // Check if it's already a valid enum value
  const enumValues = Object.values(LoyaltyProgramEnum);
  if (enumValues.includes(program as LoyaltyProgramEnum)) {
    return program as LoyaltyProgramEnum;
  }

  // Try to find by matching enum values
  const matched = enumValues.find((value) => value === program);
  return matched;
}

// The email the chain has on file for this membership. `account.email` is the
// synced inbox the membership was found in (often the sign-in alias, e.g.
// googlemail.com), so it is only a fallback.
export const getMembershipDisplayEmail = (
  account: LoyaltyAccount
): string | undefined =>
  account.membershipEmail?.trim() || account.account?.email || undefined;
